import { create } from 'zustand'
import { api } from '@/lib/api'

export interface HelpArticleMeta {
  slug: string
  title: string
  category: string
  order: number
  description: string
  /** Plain-text body projection, so search can match article text, not just
   *  titles and summaries. Derived at generation time; see backend types/help. */
  searchText: string
}

export interface HelpArticle extends HelpArticleMeta {
  body: string
}

export interface ChangelogEntry {
  slug: string
  date: string
  title: string
  body: string
}

export const CATEGORY_LABELS: Record<string, string> = {
  'getting-started': 'Getting started',
  analysis: 'Analysis',
  'drafting-with-ai': 'Drafting with AI',
  collaboration: 'Collaboration',
  library: 'Library',
  account: 'Account',
}

// Entry dates are YYYY-MM-DD, the cursor is a full ISO timestamp; comparing the
// date against the timestamp's date prefix keeps both in the same granularity.
// The seen-write (POST /help/changelog/seen) advances the cursor with
// GREATEST(now(), newestEntryDate), so at open time the cursor's date prefix is
// guaranteed >= the newest visible entry's date — clearing the dot even when a
// ship-day (AU-local) entry is still UTC-future.
export function countUnseen(entries: ChangelogEntry[], lastSeenAt: string | null): number {
  if (!lastSeenAt) return entries.length
  const seenDate = lastSeenAt.slice(0, 10)
  return entries.filter((e) => e.date > seenDate).length
}

interface HelpState {
  articles: HelpArticleMeta[]
  articlesLoaded: boolean
  isLoadingArticles: boolean
  currentArticle: HelpArticle | null
  isLoadingArticle: boolean
  articleError: boolean
  changelog: ChangelogEntry[]
  lastSeenAt: string | null
  changelogLoaded: boolean

  fetchArticles: () => Promise<void>
  fetchArticle: (slug: string) => Promise<void>
  fetchChangelog: () => Promise<void>
  markChangelogSeen: () => Promise<void>
}

export const useHelpStore = create<HelpState>()((set, get) => ({
  articles: [],
  articlesLoaded: false,
  isLoadingArticles: false,
  currentArticle: null,
  isLoadingArticle: false,
  articleError: false,
  changelog: [],
  lastSeenAt: null,
  changelogLoaded: false,

  fetchArticles: async () => {
    if (get().articlesLoaded || get().isLoadingArticles) return
    set({ isLoadingArticles: true })
    try {
      const data = await api.get<{ articles: HelpArticleMeta[] }>('/help/articles')
      set({ articles: data.articles, articlesLoaded: true, isLoadingArticles: false })
    } catch {
      set({ isLoadingArticles: false })
    }
  },

  fetchArticle: async (slug: string) => {
    set({ isLoadingArticle: true, articleError: false, currentArticle: null })
    try {
      const data = await api.get<{ article: HelpArticle }>(`/help/articles/${slug}`)
      set({ currentArticle: data.article, isLoadingArticle: false })
    } catch {
      set({ articleError: true, isLoadingArticle: false })
    }
  },

  fetchChangelog: async () => {
    try {
      const data = await api.get<{ entries: ChangelogEntry[]; lastSeenAt: string | null }>('/help/changelog')
      set({ changelog: data.entries, lastSeenAt: data.lastSeenAt, changelogLoaded: true })
    } catch {
      // Silently fail — the dot just won't show
    }
  },

  markChangelogSeen: async () => {
    const previous = get().lastSeenAt
    set({ lastSeenAt: new Date().toISOString() })
    try {
      const data = await api.post<{ lastSeenAt: string }>('/help/changelog/seen')
      set({ lastSeenAt: data.lastSeenAt })
    } catch {
      set({ lastSeenAt: previous })
    }
  },
}))
